import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { bracketMatching, indentOnInput } from '@codemirror/language';
import { lintKeymap } from '@codemirror/lint';
import { Compartment, EditorState, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import type { DiagnosticRow } from '@nowline/browser';
import { useEffect, useRef } from 'react';
import { nowlineKeymap, nowlineLanguage } from './grammar.ts';
import { buildThemeExtensions } from './highlight.ts';
import {
    createLspClient,
    type LspClient,
    lspAutocomplete,
    lspDefinition,
    lspHover,
    lspLint,
    lspReferences,
    lspToDiagnosticRow,
} from './lsp/index.ts';

export interface EditorProps {
    value: string;
    onChange: (doc: string) => void;
    /** Called on every view update; use this to observe the EditorView. */
    onState?: (view: EditorView) => void;
    /**
     * Spawn the `@nowline/lsp-worker` and attach the five LSP extensions
     * (lint, autocomplete, hover, definition, references). Defaults to
     * `true` so Free's editor pane gets the full IDE surface without any
     * extra wiring; Pro can flip it off for read-only / preview-only
     * surfaces in m6.
     */
    enableLsp?: boolean;
    /**
     * Fired whenever the LSP worker publishes diagnostics. Free's App
     * shell forwards these into `@nowline/preview-shell`'s diagnostic
     * table so the user sees the worker's verdict in both the editor
     * gutter (via `lspLint`) and the preview pane.
     */
    onDiagnostics?: (rows: DiagnosticRow[]) => void;
    /**
     * The resolved colour scheme for the editor. Drives the CodeMirror
     * theme Compartment — background, gutters, selection, and syntax
     * highlight palette all swap atomically when this changes.
     */
    theme?: 'light' | 'dark';
    /**
     * Disallow edits (a viewer, an anonymous reader, a lapsed organization).
     * The document still scrolls and its text can be copied; LSP diagnostics
     * and hover keep working.
     */
    readOnly?: boolean;
    /**
     * Extra CodeMirror extensions, held in their own compartment and
     * reconfigured when the prop changes. Pro passes its Yjs binding
     * (`yCollab`) here; the widget itself stays Yjs-agnostic.
     */
    extensions?: Extension;
    /**
     * The document is owned by something else, typically a Yjs binding in
     * `extensions`: the widget keeps no undo history of its own (the
     * binding's undo manager replaces it) and never replaces the document
     * from `value`, which then only seeds the view at mount. Read once at
     * mount; to bind a different shared document, remount (`key`).
     */
    collaborative?: boolean;
    className?: string;
}

export function Editor({
    value,
    onChange,
    onState,
    enableLsp = true,
    onDiagnostics,
    theme = 'light',
    readOnly = false,
    extensions = EMPTY,
    collaborative = false,
    className,
}: EditorProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);
    // Compartment for the LSP extension bundle so it can be hot-swapped
    // in once the worker finishes its `initialize` handshake without
    // recreating the EditorView.
    const lspCompartmentRef = useRef<Compartment>(new Compartment());
    // Compartment for editor chrome (background, gutters, cursor) and syntax
    // highlighting — swapped atomically so both layers update together.
    const themeCompartmentRef = useRef<Compartment>(new Compartment());
    const readOnlyCompartmentRef = useRef<Compartment>(new Compartment());
    const initialReadOnlyRef = useRef(readOnly);
    const extensionsCompartmentRef = useRef<Compartment>(new Compartment());
    const initialExtensionsRef = useRef(extensions);
    const collaborativeRef = useRef(collaborative);
    // Capture the initial theme so the one-time view-creation effect can
    // seed the compartment without closing over a potentially stale prop.
    const initialThemeRef = useRef(theme);

    // Keep callback refs stable so the EditorView listener never needs rebuilding.
    const onChangeRef = useRef(onChange);
    const onStateRef = useRef(onState);
    const onDiagnosticsRef = useRef(onDiagnostics);
    useEffect(() => {
        onChangeRef.current = onChange;
    });
    useEffect(() => {
        onStateRef.current = onState;
    });
    useEffect(() => {
        onDiagnosticsRef.current = onDiagnostics;
    });

    // Create the EditorView once on mount; clean up on unmount.
    // biome-ignore lint/correctness/useExhaustiveDependencies: initial `value` is intentionally captured once; subsequent changes are reconciled by the effect below
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const view = new EditorView({
            state: EditorState.create({
                doc: value,
                extensions: [
                    lineNumbers(),
                    collaborativeRef.current ? [] : history(),
                    indentOnInput(),
                    bracketMatching(),
                    keymap.of([
                        ...nowlineKeymap,
                        ...defaultKeymap,
                        ...(collaborativeRef.current ? [] : historyKeymap),
                        // F8 / Shift-F8: next/prev diagnostic; also drives the lint panel nav.
                        ...lintKeymap,
                    ]),
                    nowlineLanguage,
                    EditorView.updateListener.of((update) => {
                        if (update.docChanged) {
                            onChangeRef.current(update.state.doc.toString());
                        }
                        onStateRef.current?.(update.view);
                    }),
                    // Fixed structural theme — height + scroller + LSP hover tooltip.
                    EditorView.theme({
                        '&': { height: '100%' },
                        '.cm-scroller': { overflow: 'auto' },
                        '.cm-tooltip-lsp-hover': {
                            maxWidth: '420px',
                            padding: '6px 8px',
                            whiteSpace: 'pre-wrap',
                            font: 'inherit',
                            fontSize: '0.85em',
                            lineHeight: '1.35',
                        },
                    }),
                    // Theme compartment: editor chrome colours + syntax highlighting.
                    // Reconfigured atomically when the app colour scheme changes.
                    themeCompartmentRef.current.of(buildThemeExtensions(initialThemeRef.current)),
                    // Compartment starts empty; the LSP effect below
                    // reconfigures it with the five extension factories
                    // once the worker handshakes.
                    lspCompartmentRef.current.of([]),
                    readOnlyCompartmentRef.current.of(
                        readOnlyExtensions(initialReadOnlyRef.current),
                    ),
                    extensionsCompartmentRef.current.of(initialExtensionsRef.current),
                ],
            }),
            parent: container,
        });

        viewRef.current = view;
        return () => {
            view.destroy();
            viewRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally empty — initial `value` is baked into EditorState.create

    // Reconfigure editor chrome + syntax highlighting when the colour scheme changes.
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({
            effects: themeCompartmentRef.current.reconfigure(buildThemeExtensions(theme)),
        });
    }, [theme]);

    useEffect(() => {
        viewRef.current?.dispatch({
            effects: readOnlyCompartmentRef.current.reconfigure(readOnlyExtensions(readOnly)),
        });
    }, [readOnly]);

    const prevExtensionsRef = useRef(extensions);
    useEffect(() => {
        if (extensions === prevExtensionsRef.current) return;
        prevExtensionsRef.current = extensions;
        viewRef.current?.dispatch({
            effects: extensionsCompartmentRef.current.reconfigure(extensions),
        });
    }, [extensions]);

    // Propagate external value changes into the live view without recreating
    // it. A collaborative editor's document belongs to its binding.
    const prevValueRef = useRef(value);
    useEffect(() => {
        const view = viewRef.current;
        if (!view || collaborativeRef.current || value === prevValueRef.current) return;

        const current = view.state.doc.toString();
        if (current !== value) {
            view.dispatch({
                changes: { from: 0, to: current.length, insert: value },
            });
        }
        prevValueRef.current = value;
    }, [value]);

    // Spin the LSP worker up / down whenever `enableLsp` flips. Kept
    // separate from the view-creation effect so toggling LSP support
    // doesn't tear the editor down (preserves cursor, undo history,
    // scroll position, etc.).
    useEffect(() => {
        if (!enableLsp) return;
        const view = viewRef.current;
        if (!view) return;

        let cancelled = false;
        let client: LspClient | null = null;
        let diagnosticsSub: { dispose(): void } | null = null;

        void createLspClient()
            .then((c) => {
                if (cancelled) {
                    void c.dispose();
                    return;
                }
                client = c;
                // Call attach() *immediately before* dispatching the
                // reconfigure: attach() sends `didOpen` with the view's
                // current document text, and the returned ViewPlugin
                // must be in the state before the next docChange so we
                // don't miss the first incremental delta.
                const syncExt: Extension = c.attach(view);
                view.dispatch({
                    effects: lspCompartmentRef.current.reconfigure([
                        syncExt,
                        lspLint(c),
                        lspAutocomplete(c),
                        lspHover(c),
                        lspDefinition(c),
                        lspReferences(c),
                    ]),
                });
                diagnosticsSub = c.onDiagnostics((params) => {
                    if (params.uri !== c.uri) return;
                    const rows = params.diagnostics.map((d) => lspToDiagnosticRow(d, c.uri));
                    onDiagnosticsRef.current?.(rows);
                });
            })
            .catch((err) => {
                // Surface a startup failure to the console but keep the
                // editor itself functional — better to lose LSP features
                // than to brick the textarea.
                console.error('[nowline-editor] failed to start LSP worker', err);
            });

        return () => {
            cancelled = true;
            diagnosticsSub?.dispose();
            diagnosticsSub = null;
            const v = viewRef.current;
            if (v) {
                v.dispatch({
                    effects: lspCompartmentRef.current.reconfigure([]),
                });
            }
            if (client) {
                void client.dispose();
                client = null;
            }
        };
    }, [enableLsp]);

    return <div ref={containerRef} className={className} />;
}

const EMPTY: Extension = [];

function readOnlyExtensions(readOnly: boolean): Extension {
    return readOnly ? [EditorState.readOnly.of(true), EditorView.editable.of(false)] : [];
}
